import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface DropdownOption {
  value: string | number | null;
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  badge?: string | number;
}

interface DropdownProps {
  // Required
  options: DropdownOption[];
  value: string | number | null;
  onChange: (value: string | number | null) => void;
  
  // Optional
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  maxHeight?: string;
  className?: string;
  dropdownClassName?: string;
  label?: string;
  
  // Custom rendering
  renderOption?: (option: DropdownOption, isSelected: boolean) => React.ReactNode;
  renderSelected?: (option: DropdownOption) => React.ReactNode;
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = "Select an option...",
  disabled = false,
  loading = false,
  icon,
  maxHeight = "max-h-56",
  className = "",
  dropdownClassName = "",
  label,
  renderOption,
  renderSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Get selected option
  const selectedOption = options.find(opt => opt.value === value);

  // ✅ INDUSTRY STANDARD: Measure and position using useLayoutEffect
  // Inspired by Radix UI, Floating UI, and Headless UI patterns
  const updatePosition = useCallback(() => {
    if (!dropdownRef.current) return;
    
    const trigger = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Calculate max height based on maxHeight prop
    const maxHeightValue = maxHeight === 'max-h-56' ? 224 : maxHeight === 'max-h-64' ? 256 : 224;
    
    // Measure actual dropdown height or estimate
    let dropdownHeight = maxHeightValue;
    if (menuRef.current) {
      const actualHeight = menuRef.current.scrollHeight;
      dropdownHeight = Math.min(actualHeight, maxHeightValue);
    } else {
      // Estimate: each option ~48px + borders/padding
      const estimatedHeight = options.length * 48 + 8;
      dropdownHeight = Math.min(estimatedHeight, maxHeightValue);
    }
    
    // Calculate available space
    const spaceBelow = viewportHeight - trigger.bottom - 8;
    const spaceAbove = trigger.top - 8;
    
    // Flip upward if not enough space below
    const shouldOpenUpward = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
    
    // Calculate vertical position
    let top: number;
    if (shouldOpenUpward) {
      top = Math.max(8, trigger.top - dropdownHeight - 4);
    } else {
      top = trigger.bottom + 4;
    }
    
    // Calculate horizontal position (aligned with trigger)
    let left = trigger.left;
    const width = trigger.width;
    
    // Keep within viewport bounds
    if (left + width > viewportWidth - 8) {
      left = Math.max(8, viewportWidth - width - 8);
    }
    if (left < 8) {
      left = 8;
    }
    
    setPosition({
      top: Math.round(top),
      left: Math.round(left),
      width: Math.round(width)
    });
  }, [options.length, maxHeight]);

  // ✅ Update position when dropdown opens/closes
  useLayoutEffect(() => {
    if (isOpen) {
      // Calculate immediately when opening
      updatePosition();
    } else {
      // Clear position when closing to prevent stale values
      setPosition(null);
    }
  }, [isOpen, updatePosition]);

  // Close dropdown when clicking outside (handles Portal menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen) {
        const clickedTrigger = dropdownRef.current?.contains(event.target as Node);
        const clickedMenu = menuRef.current?.contains(event.target as Node);
        
        // Close if clicked outside both trigger and menu
        if (!clickedTrigger && !clickedMenu) {
          setIsOpen(false);
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  // ✅ Update position on scroll/resize - Industry standard pattern
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  // Handle selection
  const handleSelect = (option: DropdownOption) => {
    if (!option.disabled) {
      onChange(option.value);
      setIsOpen(false);
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">
          {label}
        </label>
      )}
      
      <div className="relative" ref={dropdownRef}>
        {/* Trigger Button */}
        <button
          type="button"
          onClick={() => {
            if (!disabled && !loading) {
              setIsOpen(!isOpen);
            }
          }}
          disabled={disabled || loading}
          className="w-full px-4 py-2.5 text-sm border-2 border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-slate-800 text-left flex items-center justify-between disabled:bg-gray-50 dark:disabled:bg-slate-700 disabled:cursor-not-allowed transition-all hover:border-gray-400 dark:hover:border-slate-500"
        >
          <div className="flex items-center flex-1 min-w-0">
            {icon && <span className="mr-2.5 shrink-0">{icon}</span>}
            <span className="text-gray-900 dark:text-slate-100 font-medium truncate">
              {loading ? 'Loading...' : selectedOption ? (
                renderSelected ? renderSelected(selectedOption) : selectedOption.label
              ) : placeholder}
            </span>
          </div>
          <ChevronDown 
            className={`h-4 w-4 text-gray-500 dark:text-slate-400 transition-transform ml-2 shrink-0 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
          />
        </button>

        {/* Dropdown Menu - Portal Rendered to Body */}
        {isOpen && !disabled && !loading && position && ReactDOM.createPortal(
          <div 
            ref={menuRef}
            style={{
              position: 'fixed',
              top: `${position.top}px`,
              left: `${position.left}px`,
              width: `${position.width}px`,
              zIndex: 9999,
              maxHeight: maxHeight === 'max-h-56' ? '14rem' : maxHeight === 'max-h-64' ? '16rem' : '14rem',
              opacity: 1,
              transform: 'translateY(0)',
              transition: 'opacity 150ms ease-out, transform 150ms ease-out'
            }}
            className={`
              bg-white dark:bg-slate-800 
              border-2 border-gray-300 dark:border-slate-600 
              rounded-lg 
              shadow-2xl 
              overflow-hidden
              ${dropdownClassName}
            `}
          >
            <div 
              className="overflow-y-auto overflow-x-hidden dropdown-scrollbar"
              style={{
                maxHeight: maxHeight === 'max-h-56' ? '14rem' : maxHeight === 'max-h-64' ? '16rem' : '14rem'
              }}
            >
            {options.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 dark:text-slate-400 text-center">
                No options available
              </div>
            ) : (
              options.map((option) => {
                const isSelected = option.value === value;
                
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    onClick={() => handleSelect(option)}
                    disabled={option.disabled}
                    className={`w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-slate-700 flex items-center justify-between text-sm transition-colors border-b border-gray-200 dark:border-slate-600 last:border-b-0 ${
                      isSelected ? 'bg-blue-50 dark:bg-slate-700 font-semibold' : ''
                    } ${
                      option.disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {renderOption ? (
                      renderOption(option, isSelected)
                    ) : (
                      <>
                        <div className="flex items-center flex-1 min-w-0">
                          {option.icon && (
                            <span className={`mr-3 shrink-0 ${
                              isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-slate-400'
                            }`}>
                              {option.icon}
                            </span>
                          )}
                          <span className={`truncate ${
                            isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-slate-200'
                          }`}>
                            {option.label}
                          </span>
                        </div>
                        {option.badge && (
                          <span className="ml-2 px-2 py-0.5 bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 text-xs rounded-full shrink-0 font-medium">
                            {option.badge}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })
            )}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default Dropdown;

